import { alpha, lighten, type Theme } from '@mui/material/styles'

/** Which way a status reads: towards access and health, or away from it. */
export type StatusToneKind = 'positive' | 'negative'

interface StatusTone {
  /** The hue at full strength, which the fills are drawn from. */
  base: string
  /** The shade of it that stays legible in this mode, for text and borders. */
  readable: string
}

/**
 * The colours a status wears, per mode.
 *
 * The two hues are read differently on purpose. `success` ships real shades, and
 * they hold up better than anything derived from its `main`, which is too pale
 * against a light surface. `error` ships surface tints instead — near-white and
 * near-black — so its red is taken from `main` and brightened for dark mode.
 */
export const statusTone = (theme: Theme, kind: StatusToneKind): StatusTone => {
  const isDark = theme.palette.mode === 'dark'

  if (kind === 'positive') {
    const { main, light, dark } = theme.palette.success
    return { base: main, readable: isDark ? light : dark }
  }

  const { main } = theme.palette.error
  return { base: main, readable: isDark ? lighten(main, 0.35) : main }
}

/**
 * A status as text on a tint of its own hue, inside a softened border of the same.
 *
 * Shared so the chip that reports a state and the action that changes it cannot
 * drift into two vocabularies for one idea.
 */
export const statusToneStyles = (theme: Theme, kind: StatusToneKind) => {
  const { base, readable } = statusTone(theme, kind)

  return {
    color: readable,
    border: `1px solid ${alpha(readable, 0.4)}`,
    backgroundColor: alpha(base, theme.palette.mode === 'dark' ? 0.12 : 0.1),
  }
}
